import { useState } from 'react'
import { useProfileSettings } from './hooks/useProfileSettings'
import ProfileSection from './components/ProfileSection'
import FeedbackSection from './components/FeedbackSection'
import DeleteAccountSection from './components/DeleteAccountSection'
import CustomConfirmModal from '../common/CustomConfirmModal'
import LanguageSwitcher from '../LanguageSwitcher'
import { useTranslation } from 'react-i18next'
import type { NotifyType } from '@/types'
import './Settings.css'

interface NotificationState {
    isOpen: boolean
    title: string
    message: string
    type: NotifyType
    callback: (() => void) | null
}

interface SettingsProps {
    onBack: () => void
}

export default function Settings({ onBack }: Readonly<SettingsProps>) {
    const { t } = useTranslation()
    const [notification, setNotification] = useState<NotificationState>({
        isOpen: false,
        title: '',
        message: '',
        type: 'info',
        callback: null,
    })

    const { profile, isLoading, updateProfile, uploadAvatar, requestPasswordReset } = useProfileSettings()

    const showNotify = (title: string, message: string, type: NotifyType = 'info', callback: (() => void) | null = null) => {
        setNotification({ isOpen: true, title, message, type, callback })
    }

    if (!profile) {
        return <div className="settings-container">{t('settings.loading')}</div>
    }

    return (
        <div className="settings-container">
            <button onClick={onBack} className="back-btn">
                <span>←</span> {t('settings.back')}
            </button>

            <div className="settings-header">
                <h2 className="settings-title">
                    <span>⚙️</span> {t('settings.title')}
                </h2>
                <p className="settings-subtitle">{t('settings.subtitle')}</p>
            </div>

            <LanguageSwitcher />

            {/* Profile & Security Sections */}
            <ProfileSection
                profile={profile}
                onUpdateProfile={updateProfile}
                onAvatarUpload={uploadAvatar}
                onPasswordReset={requestPasswordReset}
                showNotify={showNotify}
                isLoading={isLoading}
            />

            {/* Feedback Section */}
            <FeedbackSection userEmail={profile.email || ''} showNotify={showNotify} isLoading={isLoading} />

            {/* Delete Account Section */}
            <DeleteAccountSection showNotify={showNotify} isLoading={isLoading} />

            {/* Test Sentry Error Section */}
            {/*<div className="settings-section">
                <h3>Test Monitoring</h3>
                <button
                    onClick={() => {
                        throw new Error('Sentry Test Error: This is a manually triggered error to verify Sentry integration.')
                    }}
                    className="action-btn danger-btn"
                    style={{ marginTop: '1rem' }}
                >
                    Throw Test Error
                </button>
            </div>*/}

            {/* Notification Modal */}
            <CustomConfirmModal
                isOpen={notification.isOpen}
                type={notification.type}
                title={notification.title}
                message={notification.message}
                showCancel={false}
                confirmText="Entendido"
                onConfirm={() => {
                    setNotification({ ...notification, isOpen: false })
                    if (notification.callback) notification.callback()
                }}
            />
        </div>
    )
}
